import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { useCallback, useMemo } from 'react';

// Best-effort haptic feedback for user actions.  Each method swallows
// errors and is a no-op on web (Capacitor exposes Haptics there but
// most browsers won't honor it).  Callers should treat haptics as
// fire-and-forget polish, not as a signal that anything actually
// happened.
//
// Usage:
//
//   const haptics = useHaptics();
//   <Button onClick={() => { haptics.tap(); handle(); }} />
//
// Categories:
//   tap      — light feedback for any tap on a primary surface
//   impact   — explicit weight (light/medium/heavy)
//   success  — operation succeeded; pair with toast
//   warning  — non-fatal warning; pair with toast
//   error    — operation failed / destructive cancel
//   selection— wheel-style picker selection change
//
// Designing for haptics: don't fire on every render, scroll, or
// hover.  Reserve them for intentional user-initiated commits.

const fireImpact = async style => {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await Haptics.impact({ style });
  } catch {
    // Plugin unavailable or platform doesn't support it — ignore.
  }
};

const fireNotification = async type => {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await Haptics.notification({ type });
  } catch {
    // Ignore.
  }
};

const fireSelection = async () => {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await Haptics.selectionStart();
    await Haptics.selectionChanged();
    await Haptics.selectionEnd();
  } catch {
    // Ignore.
  }
};

export const useHaptics = () => {
  const tap = useCallback(() => fireImpact(ImpactStyle.Light), []);
  const impact = useCallback(style => fireImpact(style || ImpactStyle.Medium), []);
  const success = useCallback(() => fireNotification(NotificationType.Success), []);
  const warning = useCallback(() => fireNotification(NotificationType.Warning), []);
  const error = useCallback(() => fireNotification(NotificationType.Error), []);
  const selection = useCallback(() => fireSelection(), []);

  return useMemo(
    () => ({ tap, impact, success, warning, error, selection }),
    [tap, impact, success, warning, error, selection],
  );
};

// Direct imperative API for non-React callers (utilities, action
// handlers outside the render tree).  Same semantics as the hook.
export const haptics = {
  tap: () => fireImpact(ImpactStyle.Light),
  impact: style => fireImpact(style || ImpactStyle.Medium),
  success: () => fireNotification(NotificationType.Success),
  warning: () => fireNotification(NotificationType.Warning),
  error: () => fireNotification(NotificationType.Error),
  selection: fireSelection,
};
